'use client'

import { cn } from '@/core/lib/utils/cn'
import AutoPlay from 'embla-carousel-autoplay'
import useEmblaCarousel from 'embla-carousel-react'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useState } from 'react'

type Props = {
  slides: React.ReactNode[]
  slidesPerView?: number
  breakpoints?: {
    [width: number]: number
  }
  showArrows?: boolean
  showDots?: boolean
  renderPrevArrow?: (scrollPrev: () => void) => React.ReactNode
  renderNextArrow?: (scrollNext: () => void) => React.ReactNode
  renderDot?: (props: {
    index: number
    isActive: boolean
    onClick: () => void
  }) => React.ReactNode
  options?: Parameters<typeof useEmblaCarousel>[0]
  onSlideChange?: (index: number) => void
  className?: string
  autoPlay?: boolean
  delay?: number
}

export const Slider: React.FC<Props> = ({
  slides,
  slidesPerView = 1,
  breakpoints = {},
  showArrows = false,
  showDots = false,
  renderPrevArrow,
  renderNextArrow,
  renderDot,
  options,
  onSlideChange,
  className = '',
  autoPlay,
  delay = 1000,
}) => {
  const t = useTranslations()
  const isRTL =
    typeof document !== 'undefined' && document.documentElement.dir === 'rtl'
  const [emblaRef, emblaApi] = useEmblaCarousel(
    options || {
      direction: isRTL ? 'rtl' : 'ltr',
      align: isRTL ? 'end' : 'start',
      dragFree: false,
      loop: true,
    },
    [AutoPlay({ playOnInit: autoPlay, delay })],
  )

  const [currentIndex, setCurrentIndex] = useState(0)
  const [visibleSlides, setVisibleSlides] = useState(slidesPerView)

  const updateSlidesPerView = useCallback(() => {
    const width = window.innerWidth
    let newSlidesPerView = slidesPerView
    Object.keys(breakpoints)
      .map(Number)
      .sort((a, b) => a - b)
      .forEach(bp => {
        if (width >= bp) {
          newSlidesPerView = breakpoints[bp]
        }
      })
    setVisibleSlides(newSlidesPerView)
  }, [breakpoints, slidesPerView])

  useEffect(() => {
    updateSlidesPerView()
    window.addEventListener('resize', updateSlidesPerView)
    return () => window.removeEventListener('resize', updateSlidesPerView)
  }, [updateSlidesPerView])

  useEffect(() => {
    if (!emblaApi) return

    const onSelect = () => {
      const index = emblaApi.selectedScrollSnap()
      setCurrentIndex(index)
      if (onSlideChange) {
        onSlideChange(index)
      }
    }

    emblaApi.on('select', onSelect)
    onSelect()

    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi, onSlideChange])

  const scrollPrev = () =>
    isRTL ? emblaApi?.scrollNext() : emblaApi?.scrollPrev()
  const scrollNext = () =>
    isRTL ? emblaApi?.scrollPrev() : emblaApi?.scrollNext()
  const scrollTo = (index: number) => emblaApi?.scrollTo(index)

  const slideCount = slides.length - visibleSlides + 1

  return (
    <div className={cn('relative w-full')}>
      <div className='h-full cursor-grab overflow-hidden' ref={emblaRef}>
        <div className='flex'>
          {slides.map((slide, idx) => (
            <div
              key={idx}
              className={`flex-[0_0_auto] px-1 ${className}`}
              style={{ width: `${100 / visibleSlides}%` }}
            >
              {slide}
            </div>
          ))}
        </div>
      </div>

      {/* Arrows */}
      {showArrows && visibleSlides < slides.length ? (
        <>
          {renderPrevArrow ? (
            renderPrevArrow(scrollPrev)
          ) : (
            <button
              onClick={scrollPrev}
              className='absolute top-1/2 left-2 z-10 -translate-y-1/2 bg-black/60 px-2 py-1 text-white'
            >
              {isRTL ? '›' : '‹'}
            </button>
          )}
          {renderNextArrow ? (
            renderNextArrow(scrollNext)
          ) : (
            <button
              onClick={scrollNext}
              className='absolute top-1/2 right-2 z-10 -translate-y-1/2 bg-black/60 px-2 py-1 text-white'
            >
              {isRTL ? '‹' : '›'}
            </button>
          )}
        </>
      ) : null}

      {/* Dots */}
      {showDots && visibleSlides < slides.length ? (
        <div className='mt-4 flex justify-center gap-2'>
          {Array.from({ length: slideCount }).map((_, idx) =>
            renderDot ? (
              renderDot({
                index: idx,
                isActive: currentIndex === idx,
                onClick: () => scrollTo(idx),
              })
            ) : (
              <button
                key={idx}
                aria-label={t('ariaLabels.goToSlide', { slide: idx })}
                onClick={() => scrollTo(idx)}
                className={cn(
                  'h-3 w-3 cursor-pointer rounded-full transition-all',
                  currentIndex === idx
                    ? 'bg-creamed-cron'
                    : 'bg-anti-flash border-creamed-cron border',
                )}
              />
            ),
          )}
        </div>
      ) : null}
    </div>
  )
}
